import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  List,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconCheck, IconPackage, IconX } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import type { MarketplaceRelease } from "./types";
import { RELEASE_STATUS_COLORS, formatDate } from "./types";

export function ReleasesTab({
  releases,
  appId,
}: {
  releases: MarketplaceRelease[];
  appId: string;
}) {
  const navigate = useNavigate();

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={5}>Releases & Bundles</Title>
          <Text size="sm" c="dimmed">
            Release history with bundle details and review results.
          </Text>
        </div>
        <Button
          variant="light"
          onClick={() => navigate(`/developer/apps/${appId}/releases`)}
        >
          Create Release
        </Button>
      </Group>

      {releases.length === 0 && (
        <Card withBorder padding="xl">
          <Text c="dimmed" ta="center" py="md">
            No releases yet. Create your first release to submit for review.
          </Text>
        </Card>
      )}

      {releases.map((release) => (
        <Card key={release.id} withBorder>
          <Stack gap="sm">
            {/* Header row */}
            <Group justify="space-between">
              <Group gap="sm">
                <Text fw={600}>v{release.version}</Text>
                <Badge
                  color={RELEASE_STATUS_COLORS[release.status] ?? "gray"}
                  variant="light"
                >
                  {release.status.replace(/_/g, " ")}
                </Badge>
              </Group>
              <Text size="xs" c="dimmed">
                {formatDate(release.createdAt)}
              </Text>
            </Group>

            {/* Release notes */}
            {release.releaseNotes && (
              <Text size="sm" c="dimmed">
                {release.releaseNotes}
              </Text>
            )}

            {/* Bundles */}
            <Group gap="md">
              <Group gap={4}>
                <IconPackage size={14} />
                <Text size="xs" fw={500}>
                  Extension Bundle:
                </Text>
                {release.extensionBundleKey ? (
                  <Code style={{ fontSize: 11 }}>
                    {release.extensionBundleKey.split("/").pop()}
                  </Code>
                ) : (
                  <Text size="xs" c="dimmed">
                    None
                  </Text>
                )}
              </Group>
              <Group gap={4}>
                <IconPackage size={14} />
                <Text size="xs" fw={500}>
                  Module Bundle:
                </Text>
                {release.moduleBundleKey ? (
                  <Code style={{ fontSize: 11 }}>
                    {release.moduleBundleKey.split("/").pop()}
                  </Code>
                ) : (
                  <Text size="xs" c="dimmed">
                    None
                  </Text>
                )}
              </Group>
            </Group>

            {/* Manifest summary */}
            <Group gap="md">
              {release.manifest.permissions.length > 0 && (
                <Text size="xs" c="dimmed">
                  {release.manifest.permissions.length} permission
                  {release.manifest.permissions.length !== 1 ? "s" : ""}
                </Text>
              )}
              {release.manifest.extensions.length > 0 && (
                <Text size="xs" c="dimmed">
                  {release.manifest.extensions.length} extension
                  {release.manifest.extensions.length !== 1 ? "s" : ""}
                </Text>
              )}
              {release.manifest.frontendModules.length > 0 && (
                <Text size="xs" c="dimmed">
                  {release.manifest.frontendModules.length} module
                  {release.manifest.frontendModules.length !== 1 ? "s" : ""}
                </Text>
              )}
            </Group>

            {/* Review result */}
            {release.reviewResult && (
              <Card withBorder padding="xs">
                <Stack gap={4}>
                  <Group gap="xs">
                    {release.reviewResult.passed ? (
                      <IconCheck size={14} color="green" />
                    ) : (
                      <IconX size={14} color="red" />
                    )}
                    <Text
                      size="xs"
                      fw={500}
                      color={release.reviewResult.passed ? "green" : "red"}
                    >
                      Review {release.reviewResult.passed ? "Passed" : "Failed"}
                    </Text>
                    {release.reviewedAt && (
                      <Text size="xs" c="dimmed">
                        on {formatDate(release.reviewedAt)}
                      </Text>
                    )}
                  </Group>
                  {!release.reviewResult.passed && (
                    <List size="xs" spacing={2}>
                      {release.reviewResult.checks
                        .filter((c) => !c.passed)
                        .map((check) => (
                          <List.Item key={check.name}>
                            <Text size="xs" c="red">
                              {check.name}
                              {check.details ? `: ${check.details}` : ""}
                            </Text>
                          </List.Item>
                        ))}
                    </List>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Card>
      ))}
    </Stack>
  );
}
